import { ADS_DISCOVERY_SESSION_KEY, ADS_LINK_RESULT_SESSION_KEY, ADS_OAUTH_PENDING_SESSION_KEY } from '@/types';

/**
 * clientStorage — 瀏覽器持久層的**鑰匙總表 + 登出清除**(R226 資安)
 * =============================================================================
 *
 * ## 為什麼要有這張表(這是一條活的缺口,不是防禦性設計)
 *
 * 登出走 [clearAllSessionStores](./sessionReset.ts),它清的是**記憶體 store**。
 * 那支的 JSDoc 從 R72 起就寫著「store 以外的 user-scoped session 痕跡(billing 的裸
 * sessionStorage key)同樣收斂在這裡清」—— 但它**唯一清過的那把**(billing checkout)
 * 在 R105 隨金流移除一起刪了。於是那個維護點從此是空的,而在它空著的期間長出了三把
 * 新的裸鑰匙,沒有一把被清:
 *
 * | key | 內容 | 誰寫的 |
 * |---|---|---|
 * | `hai.subjectMeta.v1`(local) | 商品名稱 / 品牌 / 尺碼 / 模特兒與場景名稱 | R186 |
 * | `hai.templateDemoAssets.v1`(local) | 範本示範素材落格的 upload id | P-78 |
 * | `ads_discovery`(session) | **Google Ads 客戶 id + 帳戶名清單** | R42.1 |
 *
 * 實害是共用電腦 / 交接筆電的**跨使用者資料殘留**:A 登出、B 在同一台登入,B 讀得到
 * A 的商品資料與 A 的廣告帳戶清單(devtools 一行 `localStorage` 就看得到)。
 * `ads_discovery` 尤其明確 —— 那是**別的租戶的**客戶編號。
 *
 * ## 判準:user-scoped 清、device-scoped 留
 *
 * 不是「登出就全清」。語言、主題、側欄收合、檢視模式是**這台裝置這個人的偏好**,
 * 清掉只會讓每次登入都要重設一次(而且它們不含任何他人資料)。分類寫在
 * {@link STORAGE_KEYS} 的 `scope` 欄,新增鑰匙時**必須**在那裡登記 ——
 * `clientStorage.test.ts` 會掃全 repo,任何沒登記的 storage 使用點都會讓測試紅。
 *
 * ## 為什麼 zustand persist 的那幾把也列進來
 *
 * `hai.auth` / `hai.activeJob` / `hai.checkBatch` / `current_group_id` 走 store 的
 * `clear()`,persist middleware 會把「清空後的 state」寫回去 —— 值是乾淨的,但 key 還在,
 * 內容是 `{"state":{"user":null,...}}` 這種空殼。一起 `removeItem` 沒有壞處,而且讓
 * 「登出後瀏覽器裡不該留下任何 user-scoped 痕跡」變成一句可以逐條驗證的話。
 */

export interface StorageKeySpec {
  /** key 字面值;`prefix` 為 true 表示這是前綴,實際 key 是 `${key}<動態尾巴>`。 */
  key: string;
  storage: 'local' | 'session';
  /**
   * - `user`   — 綁登入者 / 租戶,**登出必須清**
   * - `device` — 這台機器的偏好,登出保留(語言 / 主題 / 側欄 / 檢視模式 / dev 開關)
   */
  scope: 'user' | 'device';
  prefix?: boolean;
  /**
   * 碰這把鑰匙的檔案(**全部**,不是「主要那個」)。gate 測試拿它跟「全 repo 掃到
   * 有 storage 使用的檔案」對帳 —— 漏列一個就會紅。ads 的三把各有讀寫兩端,
   * one-shot 清除都發生在別的頁,所以清單天生就不只一個。
   */
  owners: readonly string[];
}

export const STORAGE_KEYS: readonly StorageKeySpec[] = [
  // ── user-scoped:登出清 ─────────────────────────────────────────────────────
  // creativeUid / user profile / wallet(zustand persist)
  {
    key: 'hai.auth',
    storage: 'session',
    scope: 'user',
    owners: ['src/stores/authStore.ts'],
  },
  // 目前工作區 gid(zustand persist;無 hai. 前綴是歷史包袱,改名會讓存量使用者的工作區選擇歸零)
  {
    key: 'current_group_id',
    storage: 'local',
    scope: 'user',
    owners: ['src/stores/groupContextStore.ts'],
  },
  // 進行中 job 快照(zustand persist)
  {
    key: 'hai.activeJob',
    storage: 'local',
    scope: 'user',
    owners: ['src/stores/activeJobStore.ts'],
  },
  // 點數 demo 的模擬訂單(只在 demo flag 開著時寫;付款是整頁導走再導回,記憶體撐不過那一趟)
  {
    key: 'hai.pointsDemoState',
    storage: 'session',
    scope: 'user',
    owners: ['src/services/fixturePointsService.ts'],
  },
  // 批次檢查進度與逐筆 badge(zustand persist;30 分 TTL)
  {
    key: 'hai.checkBatch',
    storage: 'local',
    scope: 'user',
    owners: ['src/stores/checkBatchStore.ts'],
  },
  // 商品名稱 / 品牌 / 尺碼 / 模特兒與場景名稱(等 BE canvas.subject-meta 欄位)
  {
    key: 'hai.subjectMeta.v1',
    storage: 'local',
    scope: 'user',
    owners: ['src/features/canvas/inspector/subjectMeta.ts'],
  },
  // 範本示範素材落格的 upload id(per project/node)
  {
    key: 'hai.templateDemoAssets.v1',
    storage: 'local',
    scope: 'user',
    owners: ['src/features/canvas/templateDemoRecord.ts'],
  },
  // R352 — 剪輯頁草稿(`hai.cutDraft.v1.<projectId>`)。R363 現況:`edits` 資源已接,
  // 但草稿**平時仍只在本機**(出片時才 PATCH 上 server;跨裝置同步是未來輪)。
  // 時間軸引用專案成品 / 素材庫的 asset id → user-scoped,登出必清。
  {
    key: 'hai.cutDraft.v1.',
    storage: 'local',
    scope: 'user',
    prefix: true,
    owners: ['src/features/cut/cutDraftStorage.ts'],
  },
  // R361 — 剪輯草稿 ↔ server `edits` doc 的對應(`hai.cutEditLink.v1.<projectId>` =
  // `{eid, version}`)。出片前 PATCH 這顆 edit;丟了只是下次多建一顆,無資料損失。
  // eid 是 group 資源 id → user-scoped,登出必清。
  {
    key: 'hai.cutEditLink.v1.',
    storage: 'local',
    scope: 'user',
    prefix: true,
    owners: ['src/features/cut/cutRender.ts'],
  },
  // Google Ads discovery meta —— R237(F90-1)起只存 discovery_id + 計數,
  // **不再存帳戶清單**(R42.1–R236 存整包客戶 id + 帳戶名,是 R226 點名的殘留面;
  // 4000+ 帳號的超級用戶還會炸配額)。仍是 user-scoped:discovery_id 綁本人快照。
  {
    key: ADS_DISCOVERY_SESSION_KEY,
    storage: 'session',
    scope: 'user',
    owners: [
      'src/features/ads/discoveryAccounts.ts',
      'src/features/ads/pages/AdsAccountsSelectPage.tsx',
    ],
  },
  // OAuth 往返的 one-shot 旗標(R300 起讀寫收斂在 oauthPending.ts;連結頁 / 中繼頁只呼叫它)
  {
    key: ADS_OAUTH_PENDING_SESSION_KEY,
    storage: 'session',
    scope: 'user',
    owners: ['src/features/ads/oauthPending.ts'],
  },
  // 連結結果 banner 的 one-shot 提示
  {
    key: ADS_LINK_RESULT_SESSION_KEY,
    storage: 'session',
    scope: 'user',
    owners: [
      'src/features/ads/pages/AdsAccountsSelectPage.tsx',
      'src/features/ads/pages/AdsAccountsPage.tsx',
    ],
  },

  // ── device-scoped:登出保留 ─────────────────────────────────────────────────
  // 語言 + 素材庫檢視模式(裝置偏好;不含他人資料)
  {
    key: 'hai.prefs',
    storage: 'local',
    scope: 'device',
    owners: ['src/services/prefsStore.ts'],
  },
  // 明暗主題偏好
  {
    key: 'hai.theme',
    storage: 'local',
    scope: 'device',
    owners: ['src/styles/themeMode.ts'],
  },
  // 側欄收合
  {
    key: 'hai.sidebarCollapsed',
    storage: 'local',
    scope: 'device',
    owners: ['src/components/AppSidebar.tsx'],
  },
  // R299 — 廣告串接頁的檢視方式(列表 / 卡片;D3 `D3_adsView`)。純顯示偏好,不含任何帳戶資料。
  {
    key: 'hai.adsView',
    storage: 'local',
    scope: 'device',
    owners: ['src/features/ads/pages/AdsAccountsPage.tsx'],
  },
  // R398 — 畫布「建卡預設」(影片一鍵建卡的預設模式 id;owner 的 OMNI 卡住切換預備)。
  // R425 — 同一把多存圖片建卡的預設 model id(catalog IMAGE models 之一)。
  // 內容只有模式 id / model id,無使用者 / 租戶資料 → device-scoped 登出保留。
  {
    key: 'hai.canvasDefaults',
    storage: 'local',
    scope: 'device',
    owners: ['src/stores/canvasDefaultsStore.ts'],
  },
  // i18next 語言
  {
    key: 'hai.lang',
    storage: 'local',
    scope: 'device',
    owners: ['src/i18n/index.ts'],
  },
  // 跨部署 chunk 404 的「這個 session 已重載過一次」旗標(防迴圈,清掉反而會多重載一次)
  {
    key: 'hai.chunkReload.',
    storage: 'session',
    scope: 'device',
    prefix: true,
    owners: ['src/lib/chunkReload.ts'],
  },
  // dev 診斷開關
  {
    key: 'JOBS_DEBUG',
    storage: 'local',
    scope: 'device',
    owners: ['src/lib/debugLog.ts'],
  },
  // dev 檢查 fixture 開關
  {
    key: 'hai.checkDemo',
    storage: 'local',
    scope: 'device',
    owners: ['src/services/checkService.ts'],
  },
  // dev 報表 fixture 開關
  {
    key: 'hai.reportsDemo',
    storage: 'local',
    scope: 'device',
    owners: ['src/features/ads/pages/useMatRows.ts'],
  },
  // dev 點數 fixture 開關(`1` 刷卡 / `atm` ATM 待付款)
  {
    key: 'hai.pointsDemo',
    storage: 'local',
    scope: 'device',
    owners: ['src/services/pointsService.ts'],
  },
];

/**
 * 清掉所有 user-scoped 的瀏覽器痕跡(登出 / 401 退場的單一出口之一)。
 *
 * 前綴型(`prefix: true`)要列舉現有 key 再逐一刪;`localStorage.key(i)` 在刪除中
 * 會位移,所以**先收集再刪**。
 *
 * 整支包在 try/catch:隱私模式 / sandbox 讀寫 storage 會 throw,而登出流程
 * **不能因為清不掉痕跡就中斷**(那會讓使用者卡在半登入狀態,比殘留更糟)。
 */
export function clearUserScopedStorage(): void {
  for (const spec of STORAGE_KEYS) {
    if (spec.scope !== 'user') continue;
    const store = spec.storage === 'local' ? globalThis.localStorage : globalThis.sessionStorage;
    try {
      if (!spec.prefix) {
        store?.removeItem(spec.key);
        continue;
      }
      const doomed: string[] = [];
      for (let i = 0; i < (store?.length ?? 0); i += 1) {
        const k = store?.key(i);
        if (k?.startsWith(spec.key)) doomed.push(k);
      }
      for (const k of doomed) store?.removeItem(k);
    } catch {
      /* 隱私模式 / sandbox:清不掉不擋登出 */
    }
  }
}
